import type { Context, InputValue } from '../../contract/index.js';

export const MFE_PROTOCOL = 'hen.mfe' as const;
export const MFE_PROTOCOL_VERSION = 1;

/**
 * What an `iframe` entry may set as `messageProtocol`. `hen.mfe` is the
 * default. Each other value is one partner-owned protocol with its own
 * adapter. tenant-config and the Admin hold their own copy, which must stay
 * equal.
 */
export const MESSAGE_PROTOCOLS = [MFE_PROTOCOL, 'cars-partner-vdp'] as const;

export type MessageProtocol = (typeof MESSAGE_PROTOCOLS)[number];

export function isMessageProtocol(value: unknown): value is MessageProtocol {
  return (MESSAGE_PROTOCOLS as readonly unknown[]).includes(value);
}

/** What `event.origin` is for a frame whose sandbox has no `allow-same-origin`. */
export const OPAQUE_ORIGIN = 'null';

export type MfeMessage =
  | {
      readonly protocol: typeof MFE_PROTOCOL;
      readonly version: typeof MFE_PROTOCOL_VERSION;
      readonly ready: true;
    }
  | {
      readonly protocol: typeof MFE_PROTOCOL;
      readonly version: typeof MFE_PROTOCOL_VERSION;
      readonly inputs: Context['inputs'];
    }
  | {
      readonly protocol: typeof MFE_PROTOCOL;
      readonly version: typeof MFE_PROTOCOL_VERSION;
      readonly output: string;
      readonly payload?: unknown;
    };

const SAME_ORIGIN_TOKEN = 'allow-same-origin';

function sandboxAllowsSameOrigin(sandbox: string): boolean {
  return sandbox.split(' ').includes(SAME_ORIGIN_TOKEN);
}

/** Origin a window message from this frame must carry. */
export function frameMessageOrigin(
  sandbox: string,
  planOrigin: string,
): string {
  return sandboxAllowsSameOrigin(sandbox) ? planOrigin : OPAQUE_ORIGIN;
}

/**
 * Target origin for `postMessage` into the frame. An opaque origin serializes
 * as "null", and that string is not a target origin: the call throws. The
 * message is sent to this frame's window with "*". Replies are still accepted
 * only from the opaque origin. A frame granted its real origin is addressed
 * with that origin.
 */
export function messageTargetOrigin(
  sandbox: string,
  planOrigin: string,
): string {
  return sandboxAllowsSameOrigin(sandbox) ? planOrigin : '*';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isInputValue(value: unknown): value is InputValue {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return true;
  }
  return (
    Array.isArray(value) && value.every((item) => typeof item === 'string')
  );
}

function isInputs(value: unknown): value is Context['inputs'] {
  return isRecord(value) && Object.values(value).every(isInputValue);
}

/** A protocol message, or undefined when the value should be ignored. */
export function parseMfeMessage(data: unknown): MfeMessage | undefined {
  if (
    !isRecord(data) ||
    data.protocol !== MFE_PROTOCOL ||
    data.version !== MFE_PROTOCOL_VERSION
  ) {
    return undefined;
  }
  const base = {
    protocol: MFE_PROTOCOL,
    version: MFE_PROTOCOL_VERSION,
  } as const;
  if (data.ready === true) {
    return { ...base, ready: true };
  }
  if (typeof data.output === 'string') {
    return { ...base, output: data.output, payload: data.payload };
  }
  if (isInputs(data.inputs)) {
    return { ...base, inputs: data.inputs };
  }
  return undefined;
}
